import type { SessionEvent } from "@/types";

/**
 * A room lesson's record: who was up, with whom, for how long.
 *
 * A round is a `round_played` note from the app — `{ uids, exercise?, seconds? }`
 * — and the matrix is derived from the rounds alone. Read defensively: a
 * payload field that is not there is shown as nothing, never as a crash.
 */

export interface Round {
  id: string;
  at: number;
  exercise: string;
  uids: string[];
  seconds?: number;
}

export function uidsIn(payload: Record<string, unknown> | undefined, key = "uids"): string[] {
  const value = payload?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string" && entry.length > 0);
}

export function secondsIn(payload: Record<string, unknown> | undefined): number | undefined {
  const value = payload?.seconds;
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.round(value)
    : undefined;
}

function stringIn(payload: Record<string, unknown> | undefined, key: string): string {
  const value = payload?.[key];
  return typeof value === "string" ? value.trim() : "";
}

/** The rounds of a lesson, in the order they were played. */
export function rounds(events: readonly SessionEvent[]): Round[] {
  return events
    .filter((event) => event.type === "round_played")
    .map((event) => {
      const round: Round = {
        id: event.id,
        at: event.at,
        exercise: stringIn(event.payload, "exercise"),
        uids: uidsIn(event.payload),
      };
      const seconds = secondsIn(event.payload);
      if (seconds !== undefined) round.seconds = seconds;
      return round;
    })
    .sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
}

export interface PartnerMatrix {
  /** Everybody who was up at least once, in the order given (the register's). */
  uids: string[];
  /** How often each pair was on together. Symmetric; the diagonal is zero. */
  together: (a: string, b: string) => number;
  /** How often each student was up. */
  timesUp: (uid: string) => number;
}

/**
 * Who worked with whom, over a lesson.
 *
 * Every pair in a round counts once, whatever the round's size: a round of
 * four is six pairs. `order` is the register, so the matrix reads in the
 * order the teacher knows; students not in it but in a round come last.
 */
export function partnerMatrix(played: readonly Round[], order: readonly string[] = []): PartnerMatrix {
  const up = new Map<string, number>();
  const pairs = new Map<string, number>();
  const key = (a: string, b: string) => (a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`);

  for (const round of played) {
    const members = [...new Set(round.uids)];
    for (const uid of members) up.set(uid, (up.get(uid) ?? 0) + 1);
    for (let i = 0; i < members.length; i += 1) {
      for (let j = i + 1; j < members.length; j += 1) {
        const k = key(members[i]!, members[j]!);
        pairs.set(k, (pairs.get(k) ?? 0) + 1);
      }
    }
  }

  const seen = new Set(up.keys());
  const uids = [
    ...order.filter((uid) => seen.has(uid)),
    ...[...seen].filter((uid) => !order.includes(uid)),
  ];

  return {
    uids,
    together: (a, b) => (a === b ? 0 : (pairs.get(key(a, b)) ?? 0)),
    timesUp: (uid) => up.get(uid) ?? 0,
  };
}

/** "3:00" for 180 seconds; "12:05" for 725. Never locale-specific. */
export function formatSeconds(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}
